import React from "react";
import {
  Copy,
  FileText,
  Globe,
  Image,
  KeyRound,
  Languages,
  MessageSquareText,
  Phone,
  ShieldCheck,
  Sparkles,
  Video,
  X,
} from "lucide-react";

import TemplateStatusBadge from "./TemplateStatusBadge";

function highlightVariables(text = "") {
  const parts = String(text).split(/(\{\{\s*[\w.]+\s*\}\})/g);

  return parts.map((part, index) => (
    /^\{\{.*\}\}$/.test(part) ? (
      <span
        key={index}
        className="rounded bg-[#e8f4fa] px-1 py-0.5 text-[10px] font-bold text-[#147db5] dark:bg-[#173846] dark:text-[#6fc5eb]"
      >
        {part}
      </span>
    ) : (
      <React.Fragment key={index}>{part}</React.Fragment>
    )
  ));
}

function MediaHeader({ format }) {
  const map = {
    IMAGE: { Icon: Image, label: "Image header" },
    VIDEO: { Icon: Video, label: "Video header" },
    DOCUMENT: { Icon: FileText, label: "Document header" },
  };
  const { Icon, label } = map[format] || map.DOCUMENT;

  return (
    <div className="mb-3 flex h-32 items-center justify-center gap-2 rounded-xl border border-[#d7e3e7] bg-[linear-gradient(135deg,#f3f7f8,#e9f3f6)] text-[#6f8793] dark:border-[#35505d] dark:bg-[linear-gradient(135deg,#1a303a,#172832)] dark:text-[#8ca5b1]">
      <Icon size={21} strokeWidth={1.7} />
      <span className="text-[10px] font-bold">{label}</span>
    </div>
  );
}

const buttonIcons = {
  URL: Globe,
  PHONE_NUMBER: Phone,
  COPY_CODE: Copy,
  OTP: KeyRound,
  QUICK_REPLY: null,
};

function Buttons({ buttons = [] }) {
  if (!buttons.length) return null;

  return (
    <div className="mt-3 space-y-1.5 border-t border-[#e1e8e5] pt-2.5 dark:border-[#30483f]">
      {buttons.map((button, index) => {
        const Icon = buttonIcons[button.type];

        return (
          <div
            key={`${button.type}-${index}`}
            className="flex items-center justify-center gap-1.5 rounded-lg border border-[#cee3eb] bg-[#f8fcfd] py-2 text-[10px] font-bold text-[#147db5] dark:border-[#315364] dark:bg-[#17303a] dark:text-[#6fc5eb]"
          >
            {Icon ? <Icon size={12} /> : null}
            {button.text || button.type}
          </div>
        );
      })}
    </div>
  );
}

function ComponentPreview({ components = [] }) {
  const header = components.find((component) => component.type === "HEADER");
  const body = components.find((component) => component.type === "BODY");
  const footer = components.find((component) => component.type === "FOOTER");
  const buttons = components.find((component) => component.type === "BUTTONS");
  const known = ["HEADER", "BODY", "FOOTER", "BUTTONS"];
  const others = components.filter((component) => !known.includes(component.type));

  return (
    <div className="relative max-w-[440px] rounded-[16px] rounded-tl-[4px] border border-[#e1eaef] bg-[#ffffff] p-3.5 shadow-[0_8px_24px_rgba(34,60,50,0.10)] dark:border-[#30483f] dark:bg-[#16241f]">
      {header && (header.format === "TEXT" || !header.format ? (
        <p className="mb-2 text-[12px] font-bold text-[#233d34] dark:text-[#e5eee9]">
          {highlightVariables(header.text)}
        </p>
      ) : (
        <MediaHeader format={header.format} />
      ))}

      <div className="whitespace-pre-wrap text-[11px] font-medium leading-5 text-[#3d534b] dark:text-[#c7d5ce]">
        {highlightVariables(body?.text) || "—"}
      </div>

      {footer?.text ? (
        <p className="mt-2 text-[9px] font-medium text-[#8a9ba8] dark:text-[#71867d]">{footer.text}</p>
      ) : null}

      <Buttons buttons={buttons?.buttons} />

      {others.map((component, index) => (
        <p key={`${component.type}-${index}`} className="mt-2 text-[9px] italic text-[#8a9ba8] dark:text-[#71867d]">
          Unsupported component: {component.type}
        </p>
      ))}

      <span className="absolute -left-[7px] top-0 h-3 w-3 bg-[#ffffff] [clip-path:polygon(100%_0,100%_100%,0_0)] dark:bg-[#16241f]" />
    </div>
  );
}

const TemplatePreviewModal = ({ template, onClose }) => {
  const components = Array.isArray(template?.components) ? template.components : [];

  return (
    <div className="fixed inset-0 z-[1000] flex items-center justify-center p-3 sm:p-5">
      <button
        type="button"
        className="absolute inset-0 cursor-default bg-[#102a3d]/35 backdrop-blur-[5px]"
        onClick={onClose}
        aria-label="Dismiss template preview backdrop"
      />

      <div className="relative z-10 flex max-h-[92vh] w-full max-w-[850px] flex-col overflow-hidden rounded-[26px] border border-white/80 bg-[#ffffff] shadow-[0_30px_90px_rgba(10,35,52,0.26)] dark:border-[#31505f] dark:bg-[#101f27]">
        <div className="flex items-start justify-between gap-4 border-b border-[#dce8ed] px-5 py-4 sm:px-6 dark:border-[#29404d]">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <Sparkles size={12} className="text-[#a96119] dark:text-[#e2ad6c]" />
              <p className="text-[8px] font-bold uppercase tracking-[0.16em] text-[#6f8693] dark:text-[#829aa7]">Template preview</p>
            </div>
            <h2 className="mt-2 truncate text-[17px] font-bold tracking-[-0.03em] text-[#173448] dark:text-[#edf6f9]">
              {template.name}
            </h2>
            <p className="mt-1 text-[10px] font-semibold text-[#80939e] dark:text-[#78919e]">
              {template.language} · {template.category || "—"}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close template preview"
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-[#d6e3e8] bg-[#f7fafb] text-[#728793] transition-all hover:border-[#b8d2dc] hover:bg-white hover:text-[#147db5] dark:border-[#31505f] dark:bg-[#152832] dark:text-[#91a9b6]"
          >
            <X size={15} />
          </button>
        </div>

        <div className="grid min-h-0 flex-1 overflow-y-auto md:grid-cols-[240px_minmax(0,1fr)]">
          <aside className="border-b border-[#dce8ed] bg-[#f7fafb] p-5 md:border-b-0 md:border-r dark:border-[#29404d] dark:bg-[#12242d]">
            <TemplateStatusBadge status={template.status} />

            <div className="mt-6 space-y-4">
              <div className="flex gap-3">
                <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-[10px] border border-[#b9dff0] bg-[#edf8fd] text-[#147db5] dark:border-[#28516a] dark:bg-[#142a36] dark:text-[#6fc5eb]">
                  <MessageSquareText size={14} />
                </div>
                <div>
                  <p className="text-[8px] font-bold uppercase tracking-[0.11em] text-[#8799a3] dark:text-[#708b98]">Category</p>
                  <p className="mt-1 text-[10px] font-bold text-[#294657] dark:text-[#dbe7ed]">{template.category || "—"}</p>
                </div>
              </div>
              <div className="flex gap-3">
                <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-[10px] border border-[#afe0c5] bg-[#eefaf4] text-[#167a59] dark:border-[#285444] dark:bg-[#142d25] dark:text-[#67c9a1]">
                  <Languages size={14} />
                </div>
                <div>
                  <p className="text-[8px] font-bold uppercase tracking-[0.11em] text-[#8799a3] dark:text-[#708b98]">Language</p>
                  <p className="mt-1 text-[10px] font-bold text-[#294657] dark:text-[#dbe7ed]">{template.language || "—"}</p>
                </div>
              </div>
            </div>

            <div className="mt-6 rounded-xl border border-[#d7e5ea] bg-white/80 p-3 dark:border-[#31505f] dark:bg-[#162a34]">
              <div className="flex items-center gap-2 text-[#607987] dark:text-[#91a9b6]">
                <ShieldCheck size={13} className="text-[#20a777]" />
                <p className="text-[9px] font-bold">Read-only preview</p>
              </div>
              <p className="mt-2 text-[8px] font-medium leading-4 text-[#82949e] dark:text-[#718b98]">
                Variables display as placeholders until a real message is prepared.
              </p>
            </div>
          </aside>

          <section className="relative min-h-[360px] overflow-hidden bg-[linear-gradient(135deg,#edf7f3_0%,#e8f2f5_100%)] p-5 sm:p-7 dark:bg-[linear-gradient(135deg,#11251f_0%,#132932_100%)]">
            <div
              className="pointer-events-none absolute inset-0 opacity-40"
              style={{
                backgroundImage: "radial-gradient(rgba(33,111,85,0.18) 1px, transparent 1px)",
                backgroundSize: "18px 18px",
              }}
            />
            <div className="relative">
              <div className="mb-4 flex items-center gap-2 text-[8px] font-bold uppercase tracking-[0.13em] text-[#627c70] dark:text-[#7b9a8c]">
                <span className="h-1.5 w-1.5 rounded-full bg-[#20a777]" />
                WhatsApp message preview
              </div>
              <ComponentPreview components={components} />

              {template.status === "REJECTED" && template.rejection_reason ? (
                <div className="mt-4 max-w-[440px] rounded-xl border border-[#efcecc] bg-[#fff3f2] p-3 text-[9px] font-medium leading-4 text-[#a24f4c] dark:border-[#633c3b] dark:bg-[#2b1f1e] dark:text-[#e39491]">
                  <span className="font-bold">Rejected: </span>
                  {template.rejection_reason}
                </div>
              ) : null}

              {template.sync_status === "remote_deleted" ? (
                <div className="mt-4 max-w-[440px] rounded-xl border border-[#efd5b0] bg-[#fff8eb] p-3 text-[9px] font-medium leading-4 text-[#986022] dark:border-[#5b472d] dark:bg-[#302719] dark:text-[#e2ad6c]">
                  This template no longer exists in Meta. It remains available only for historical reference.
                </div>
              ) : null}
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default TemplatePreviewModal;
